<template>
  <div>
    <div class="btn-group-hdr">
      <div class="btn-group btn-group-toggle">
        <button class="btn btn-success btn-sm" @click="downloadCsv">
          <i class="fas fa-file-csv me-1"></i> Download CSV
        </button>
      </div>
    </div>

    <div class="title-hdr bg-dark">
      {{ title }}
    </div>

    <div class="card">
      <div class="card-body">

        <!-- Filters -->
        <div class="row g-2 mb-3 align-items-end">
          <div class="col-12 col-lg-6">
            <label class="form-label mb-1">Search (space-separated tags)</label>
            <input
              v-model="q"
              class="form-control form-control-sm"
              placeholder="e.g. update role admin failed 10.10.1.5"
              @keyup.enter="applyFilters"
            />
            <div class="mt-2" v-if="tokens.length">
              <span v-for="t in tokens" :key="t" class="badge bg-secondary me-1">{{ t }}</span>
              <button class="btn btn-link btn-sm p-0 ms-2" @click="clearSearch">clear</button>
            </div>
          </div>

          <div class="col-12 col-lg-3">
            <label class="form-label mb-1">Type</label>
            <select v-model="types" class="form-select form-select-sm" multiple>
              <option value="LOGIN_HISTORY">LOGIN_HISTORY</option>
              <option value="AUDIT_TRAIL">AUDIT_TRAIL</option>
              <option value="AUDIT_LOG">AUDIT_LOG</option>
            </select>
            <small class="text-muted">Ctrl/Cmd click to multi-select</small>
          </div>

          <div class="col-12 col-lg-3 d-flex gap-2">
            <button class="btn btn-primary btn-sm w-100" @click="applyFilters">
              <i class="fas fa-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
              Reset
            </button>
          </div>
        </div>

        <!-- Data -->
        <data-viewer-2
          :source="computedSource"
          :thead="thead"
          :key="viewerKey"
          ref="logsViewer"
        >
          <template slot-scope="props">
            <tr>
              <td>{{ props.item.time }}</td>
              <td>{{ props.item.log_type }}</td>
              <td>{{ props.item.user_name }}</td>
              <td>{{ props.item.username }}</td>
              <td>{{ props.item.ip_address || '—' }}</td>
              <td>{{ props.item.status || props.item.severity || '—' }}</td>
              <td>
                <!-- Show “summary” across the three types -->
                <div v-if="props.item.log_type === 'LOGIN_HISTORY'">
                  <span class="fw-semibold">{{ props.item.status }}</span>
                  <span v-if="props.item.failure_reason" class="text-danger">
                    - {{ props.item.failure_reason }}
                  </span>
                </div>

                <div v-else-if="props.item.log_type === 'AUDIT_TRAIL'">
                  <span class="fw-semibold">{{ props.item.action_type }}</span>
                  <span class="text-muted"> on </span>
                  <span class="fw-semibold">{{ props.item.table_name }}</span>
                  <span v-if="props.item.field_name" class="text-muted"> ({{ props.item.field_name }})</span>
                </div>

                <div v-else>
                  <span class="fw-semibold">{{ props.item.module }}</span>
                  <span class="text-muted"> - </span>
                  <span class="fw-semibold">{{ props.item.action }}</span>
                  <span v-if="props.item.description" class="text-muted">: {{ props.item.description }}</span>
                </div>
              </td>
            </tr>
          </template>
        </data-viewer-2>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { bus } from '../../../main.js'
import DataViewer2 from './../common/DataViewer2.vue'

export default {
  name: 'Log',
  props: {
    hasPermission: { type: Function, required: true }
  },
  components: { DataViewer2 },
  data() {
    return {
      title: 'Logs',
      q: '',
      types: [],            // multi select
      viewerKey: 0,
    }
  },
  computed: {
    tokens() {
      if (!this.q) return []
      const parts = this.q.split(/\s+/).filter(Boolean)
      // de-dupe
      const seen = {}
      const out = []
      parts.forEach(p => {
        const k = p.toLowerCase()
        if (!seen[k]) { seen[k] = true; out.push(p) }
      })
      return out
    },
    computedSource() {
      const base = '/user/logs/paginate'
      const params = []

      if (Array.isArray(this.types) && this.types.length) {
        params.push('types=' + encodeURIComponent(this.types.join(',')))
      }

      // IMPORTANT: use '&' not '&amp;'
      return params.length ? base + '?' + params.join('&') : base
    },
    thead() {
      return [
        { title: 'Time', key: 'time', sort: true },
        { title: 'Type', key: 'log_type', sort: true },
        { title: 'Name', key: 'user_name', sort: true },
        { title: 'Username', key: 'username', sort: true },
        { title: 'IP', key: 'ip_address', sort: true },
        { title: 'Status/Severity', key: 'status', sort: true },
        { title: 'Details', key: 'details' }
      ]
    }
  },
  mounted() {
    this.$store.commit('selectedTab', 'logs')

    // bootstrap tooltips if you use them
    const list = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
    list.forEach(el => new bootstrap.Tooltip(el))

    bus.$on('nav:logs-root', this.goToLogsRoot)
  },
  beforeDestroy() {
    bus.$off('nav:logs-root', this.goToLogsRoot)
  },
  methods: {
    goToLogsRoot() {
      this.resetFilters()
    },
    applyFilters() {
      // bump key to force DataViewer reload
      this.viewerKey += 1
      this.$nextTick(() => {
        if (this.$refs.logsViewer && this.$refs.logsViewer.reload) this.$refs.logsViewer.reload()
      })
    },
    clearSearch() {
      this.q = ''
      this.applyFilters()
    },
    resetFilters() {
      this.q = ''
      this.types = []
      this.applyFilters()
    },
    async downloadCsv() {
      try {
        const params = []
        if (this.q && this.q.trim()) params.push('q=' + encodeURIComponent(this.q.trim()))
        if (this.types && this.types.length) params.push('types=' + encodeURIComponent(this.types.join(',')))

        const url = '/user/logs/export' + (params.length ? '?' + params.join('&') : '')

        const resp = await axios.get(url, { responseType: 'blob' })
        const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'logs_export.csv'
        link.click()
        window.URL.revokeObjectURL(link.href)
      } catch (e) {
        Swal && Swal.fire && Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to download CSV.'
        })
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/users/users/Log.vue